"use client";

import { motion, Variants } from "framer-motion";
import { Activity, Brain, Cpu, ShieldCheck, Terminal } from "lucide-react";

export default function Process() {
    const steps = [
        {
            icon: Terminal,
            title: "Spec & Prompting",
            desc: "Co-authoring markdown specifications and prompt templates to programmatically define expectations.",
            key: "discovery",
            color: "text-yellow-500"
        },
        {
            icon: Brain,
            title: "Cognitive Design",
            desc: "Structuring agent state-graphs, context boundaries, dynamic tools, and human-in-the-loop flows.",
            key: "design",
            color: "text-purple-500"
        },
        {
            icon: Cpu,
            title: "Agent Scaffolding",
            desc: "Spawning specialized AI developers to auto-generate codebases, scaffold databases, and write API mocks.",
            key: "development",
            color: "text-blue-500"
        },
        {
            icon: ShieldCheck,
            title: "Elite Refinement",
            desc: "Expert human engineers audit generated code, secure configurations, and build high-complexity features.",
            key: "launch",
            color: "text-orange-500"
        },
        {
            icon: Activity,
            title: "Continuous Audit",
            desc: "Deploying autonomous testing agents to simulate traffic, probe for edge cases, and ensure stability.",
            key: "support",
            color: "text-red-500"
        }
    ];

    const iconVariants: Record<string, Variants> = {
        discovery: {
            rest: { scale: 1, filter: "drop-shadow(0px 0px 0px rgba(234, 179, 8, 0))" },
            hover: {
                scale: 1.1,
                filter: "drop-shadow(0px 0px 8px rgba(234, 179, 8, 0.5))",
                transition: { duration: 0.3 }
            }
        },
        design: {
            rest: { rotate: 0, scale: 1 },
            hover: {
                rotate: [0, -10, 10, -5, 5, 0],
                transition: { duration: 0.5 }
            }
        },
        development: {
            rest: { scale: 1 },
            hover: {
                scale: [1, 1.1, 1],
                rotateX: [0, 180, 0], // Flip effect
                transition: { duration: 0.6 }
            }
        },
        launch: {
            rest: { y: 0, scale: 1 },
            hover: {
                y: -10,
                scale: 1.1,
                transition: {
                    type: "spring",
                    stiffness: 300,
                    damping: 10
                }
            }
        },
        support: {
            rest: { scale: 1 },
            hover: {
                scale: [1, 1.2, 1],
                transition: {
                    repeat: Infinity,
                    repeatType: "reverse",
                    duration: 0.8
                }
            }
        }
    };

    return (
        <section className="py-24 bg-neutral-100 border-b border-black relative overflow-hidden">
            {/* Industrial Grid Background */}
            <div className="absolute inset-0 z-0 opacity-[0.03]"
                style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="mb-20 text-center max-w-2xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Our Lifecycle</h2>
                    <p className="text-xl text-gray-500 font-mono tracking-tight">HYBRID AGENTIC WORKFLOW DESIGNED FOR <span className="text-black font-bold">VELOCITY</span>.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative">
                    {/* Connecting Line (Desktop) - Schematic Style */}
                    <div className="hidden md:block absolute top-[3rem] left-0 right-0 h-px bg-black/10 border-t border-dashed border-black/20 -z-10"></div>

                    {steps.map((step, i) => (
                        <motion.div
                            key={i}
                            className="group relative h-full"
                            initial="rest"
                            whileHover="hover"
                            viewport={{ once: true }}
                        >
                            {/* Glass Card */}
                            <div className="relative bg-white/40 backdrop-blur-md border border-white/60 p-6 shadow-sm group-hover:shadow-xl group-hover:bg-white/60 transition-all duration-300 h-full rounded-2xl flex flex-col items-center text-center">

                                {/* Step Indicator */}
                                <div className="absolute top-2 right-3 text-[10px] font-mono font-bold text-black/30 tracking-widest">
                                    {(i + 1).toString().padStart(2, '0')}
                                </div>

                                {/* Icon in Glass Box */}
                                <motion.div
                                    variants={iconVariants[step.key]}
                                    className="w-16 h-16 bg-white/80 border border-white shadow-inner flex items-center justify-center rounded-xl mb-6"
                                >
                                    <step.icon className={`h-7 w-7 opacity-80 ${step.color} transition-colors duration-300`} />
                                </motion.div>

                                <h3 className="text-lg font-bold mb-3 uppercase tracking-tight">{step.title}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed font-medium">{step.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
